import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    effect,
    inject,
    input,
    InputSignal,
    Signal,
    signal,
    untracked,
    WritableSignal,
} from '@angular/core';
import { USE_NX_TOOL_API } from '@dat/datiq-nx-repo-client';
import {
    emptyProjectQuery,
    EnvironmentKeys,
    ProjectInfo,
    Teams,
    ProjectQuery,
    LibraryTypes,
    Components,
    ProjectUpdateInfo,
} from '@dat/datiq-nx-repo-utils';
import {
    IqControlsDashboardComponent,
    Control,
    CheckboxControl,
} from '@dat/iq-controls';
import { IqStatusLibraryStateService } from '../state';
import { TableFormRowInfo } from '../models';
import { startCase } from 'lodash';
import {
    addContainerClassesNoRules,
    checkBoxLabelChecked,
    checkBoxLabelUnchecked,
    IqBuilderLabelComponent,
    createUiTypeSelectorString,
    multiClasses,
} from '@dat/iq-style-processor';
import { FormGroup } from '@angular/forms';

const domainOptionMap: Record<Components, string> = {
    Unknown: 'Unknown',
    Core: 'Core',
    BenchmarkAnalytics: 'BA',
    RateLane: 'Rateview',
    Trendlines: 'Trendlines',
    NX: 'NX',
    Builder: 'Builder',
    MarketConditions: '<C',
    Landing: 'Landing',
    RateNational: 'Rate National',
    Job: 'Job',
    RateLaneBatch: 'Rateview Batch',
    Social: 'Social',
    RFP: 'RFP',
    SharedComponents: 'Shared',
    CI_CD: 'CI/CD',
};

const maxEnvMap: Record<EnvironmentKeys, string> = {
    Unknown: 'Unknown',
    nprod: 'Test',
    staging: 'Stage',
    prod: 'Prod',
    notUsed: 'Not Used',
};

const typeOptionMap: Record<LibraryTypes, string> = {
    Unknown: 'Unknown',
    Feature: 'Feature',
    Data: 'Data',
    Utils: 'Utils',
    UI: 'UI',
    Core: 'Core',
    DesignSystem: 'Design System',
};

const teamOptionMap: Record<Teams, string> = {
    RedBarclays: 'Red Barclays',
    PorkChopExpress: 'PCE',
    BinaryBrains: 'Binary Brains',
    FrontEndSquad: 'FES',
    Unknown: 'Unknown',
    Elevation: 'Elevation',
    CrossTeam: 'AUT',
    WorksOnMyMachine: 'WOMM',
};

type ColKeys =
    | 'critical'
    | 'proj'
    | 'team'
    | 'type'
    | 'domain'
    | 'maxEnv'
    | 'key'
    | 'save';
type FormData = {
    form: {
        critical: boolean;
        proj: string;
        team: {
            value: string;
            display: string;
        };
        type: {
            value: string;
            display: string;
        };
        domain: {
            value: string;
            display: string;
        };
        maxEnv: {
            value: string;
            display: string;
        };
    };
};

@Component({
    selector: 'iq-status-library-table',
    standalone: true,
    imports: [
        CommonModule,
        IqControlsDashboardComponent,
        IqBuilderLabelComponent,
    ],
    templateUrl: './iq-status-library-table.component.html',
    styleUrl: './iq-status-library-table.component.scss',
})
export class IqStatusLibraryTableComponent {
    private _state: IqStatusLibraryStateService = inject(
        IqStatusLibraryStateService,
    );

    private _static: boolean = inject(USE_NX_TOOL_API);

    editMode: WritableSignal<boolean> = signal(false);

    canEdit: Signal<boolean> = computed(() => {
        return this._static;
    });
    id: InputSignal<string> = input<string>();
    testId: InputSignal<string> = input<string>();
    query: InputSignal<ProjectQuery> = input<ProjectQuery>({
        ...emptyProjectQuery,
    });
    currentTestId: Signal<string> = computed(() => {
        return this.testId()?.length ? this.testId() : this.id();
    });

    colKeys: ColKeys[] = [
        'proj',
        'type',
        'team',
        'domain',
        'maxEnv',
        'critical',
        'key',
        'save',
    ];

    headerControls: TableFormRowInfo = {
        id: 'header',
        containerClasses: {
            ...addContainerClassesNoRules([`grid`, `grid-cols-8`]),
        },
        controls: this.colKeys.map(
            (key): Control => ({
                id: `header-${key}`,
                name: `header-${key}`,
                type: 'Label',
                label: `${
                    { proj: 'Project', save: '' }[key] ?? startCase(key)
                }`,
            }),
        ),
    };

    projects: WritableSignal<ProjectInfo[]> = signal([]);
    allProjects: ProjectInfo[] = [];

    uneditRows: Signal<
        { id: string; cols: { id: string; label: string }[] }[]
    > = computed(() => {
        const currentProjects: ProjectInfo[] = this.projects();

        return currentProjects.map((project: ProjectInfo) => {
            return {
                id: project.key,
                cols: this.colKeys
                    .filter((key) => key !== 'save')
                    .map((colKey: ColKeys): { id: string; label: string } => ({
                        id: project.key + ' ' + colKey,
                        label: this.getTableLabel(
                            project.key,
                            colKey,
                            this.getValue(colKey, project),
                        ),
                    })),
            };
        });
    });

    rows: Signal<TableFormRowInfo[]> = computed(() => {
        const currentProjects: ProjectInfo[] = this.projects();
        const controlMap: Record<
            ColKeys,
            (value: any, info: Partial<Control>) => Control
        > = {
            key: (value: any, info: Partial<Control>): Control => {
                return {
                    id: info?.id ?? 'critical',
                    name: `projKey`,
                    type: 'Label',
                    label: `${value}`,
                };
            },
            critical: (value: any, info: Partial<Control>): Control => {
                return {
                    type: 'Checkbox',
                    id: info?.id ?? 'critical',
                    value: value ?? false,
                    name: 'critical',
                    checkedLabel: `<div class="grid grid-cols-2 gap-1">${checkBoxLabelChecked(
                        'Critical',
                    )}</div>`,
                    uncheckedLabel: `<div class="grid grid-cols-2 gap-1">${checkBoxLabelUnchecked(
                        'Critical',
                    )}</div>`,
                };
            },
            proj: (value: any, info: Partial<Control>): Control => {
                return {
                    type: 'Text',
                    id: info?.id ?? 'proj',
                    value: value ?? null,
                    name: 'proj',
                    label: 'Project',
                    disabled: true,
                };
            },
            team: (value: any, info: Partial<Control>): Control => {
                const options = Object.keys(teamOptionMap).map((team) => ({
                    value: team,
                    display: teamOptionMap[team],
                }));
                const currentValue =
                    { value, display: teamOptionMap[value] } ?? null;
                const name: string = 'team';

                return this._createSelectControl(
                    name,
                    info,
                    currentValue,
                    options,
                );
            },
            type: (value: any, info: Partial<Control>): Control => {
                const options = Object.keys(typeOptionMap).map((type) => ({
                    value: type,
                    display: typeOptionMap[type],
                }));
                const currentValue =
                    { value, display: typeOptionMap[value] } ?? null;
                const name: string = 'type';

                return this._createSelectControl(
                    name,
                    info,
                    currentValue,
                    options,
                );
            },
            domain: (value: any, info: Partial<Control>): Control => {
                const options = Object.keys(domainOptionMap).map((domain) => ({
                    value: domain,
                    display: domainOptionMap[domain],
                }));
                const currentValue =
                    { value, display: domainOptionMap[value] } ?? null;
                const name: string = 'domain';

                return this._createSelectControl(
                    name,
                    info,
                    currentValue,
                    options,
                );
            },
            maxEnv: (value: any, info: Partial<Control>): Control => {
                const options = Object.keys(maxEnvMap).map((env) => ({
                    value: env,
                    display: maxEnvMap[env],
                }));
                const currentValue =
                    { value, display: maxEnvMap[value] } ?? null;
                const name: string = 'maxEnv';

                return this._createSelectControl(
                    name,
                    info,
                    currentValue,
                    options,
                );
            },
            save: function (value: any, info: Partial<Control>): Control {
                return {
                    type: 'Submit',
                    label: 'Save',
                    id: info?.id ?? 'save',
                    name: 'save',
                };
            },
        };

        return currentProjects.map((project: ProjectInfo) => {
            const controls = this.colKeys.map((key) => {
                const control = controlMap[key](this.getValue(key, project), {
                    id: `${project.key}~${key}`,
                });
                return control;
            });

            return {
                id: project.key,
                controls,
                containerClasses: {
                    ...addContainerClassesNoRules([
                        `grid`,
                        'gap-2',
                        `grid-cols-8`,
                        'my-2',
                        'items-center',
                        'border-b-2',
                        '!border-solid',
                        'border-dat-gray-900',
                    ]),
                    ...multiClasses(
                        [
                            'rounded-md',
                            'border-2',
                            'border-dat-gray-900',
                            'border-solid',
                        ],
                        [createUiTypeSelectorString('textBox')],
                    ),
                    ...multiClasses(
                        ['bg-white', 'p-2'],
                        [createUiTypeSelectorString('textBox')],
                    ),
                    ...multiClasses(
                        ['hidden'],
                        ['.mat-mdc-form-field-subscript-wrapper'],
                    ),
                    ...multiClasses(
                        ['rounded-full', 'px-2', 'py-1', 'w-full'],
                        [createUiTypeSelectorString('button')],
                    ),
                },
                fg: new FormGroup({}),
            };
        });
    });

    private _createSelectControl(
        name: string,
        info: Partial<Control>,
        value: any,
        options: { value: string; display: any }[],
    ): Control {
        return {
            type: 'Select',
            id: info?.id ?? name,
            value,
            name,
            label: '',
            style: 'thin',
            options,
        };
    }

    getValue(key: ColKeys, project: ProjectInfo): any {
        const valueMap: Record<ColKeys, (project: ProjectInfo) => any> = {
            key: (project: ProjectInfo) => project.key,
            save: (project: ProjectInfo) => '',
            critical: (project: ProjectInfo) => project.critical,
            proj: (project: ProjectInfo) => project.proj,
            team: (project: ProjectInfo) => project.team,
            type: (project: ProjectInfo) => project.type,
            domain: (project: ProjectInfo) => project.domain,
            maxEnv: (project: ProjectInfo): EnvironmentKeys => {
                return project.prod
                    ? 'prod'
                    : project.staging
                    ? 'staging'
                    : project.nprod
                    ? 'nprod'
                    : project.notUsed
                    ? 'notUsed'
                    : 'Unknown';
            },
        };

        return valueMap?.[key]?.(project) ?? null;
    }

    pageSize: number = 35;

    maxProjects: WritableSignal<number> = signal(this.pageSize);

    constructor() {
        effect(async () => {
            const query: ProjectQuery = this.query();

            this.allProjects = await this._state.queryProjects(query);

            untracked(() => {
                this.maxProjects.set(this.pageSize);
                this.projects.set(
                    this.allProjects?.slice(0, this.maxProjects()),
                );
            });
        });

        effect(() => {
            const maxProjects = this.maxProjects();

            untracked(() => {
                this.projects.set(this.allProjects.slice(0, maxProjects));
            });
        });
    }

    loadMore(): void {
        this.maxProjects.set(this.maxProjects() + this.pageSize);
    }

    onSubmit(projectKey: string, submitInfo: FormData) {
        const request: Partial<ProjectUpdateInfo> = {
            proj: submitInfo.form.proj,
            team: submitInfo.form.team.value as Teams,
            type: submitInfo.form.type.value as LibraryTypes,
            domain: submitInfo.form.domain.value as Components,
            maxEnv: submitInfo.form.maxEnv.value as EnvironmentKeys,
            critical: submitInfo.form.critical,
        };

        this._state.updateProject(projectKey, {
            ...request,
        });
    }

    async toggleEditMode() {
        if (!this.editMode()) {
            this.editMode.set(true);
            return;
        }

        try {
            this.allProjects = await this._state.queryProjects(this.query());
            this.maxProjects.set(this.pageSize);
            this.projects.set(this.allProjects?.slice(0, this.maxProjects()));
            this.editMode.set(!this.editMode());
        } catch (error) {
            this.editMode.set(!this.editMode());
        }
    }

    getTableLabel(projectKey: string, name: string, value: any): string {
        return {
            proj: (value) => `${value}`,
            key: (value) => `${projectKey}`,
            team: (value) => `${teamOptionMap[value]}`,
            type: (value) => `${typeOptionMap[value]}`,
            domain: (value) => `${domainOptionMap[value]}`,
            maxEnv: (value) => `${maxEnvMap[value]}`,
            critical: (value) => `${value}`,
        }?.[name]?.(value);
    }

    export(): void {
        this._state.exportProjects(this.query());
    }
}
